/**
 * Pure H OS · Phase D Wave 4 · Analytics.
 * Directory of live dashboards, deep-dives and ad-hoc analyses.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { ANALYTICS, AnalyticsKind, AnalyticsStatus } from "../mocks/analyticsData";

const KIND_LABEL: Record<AnalyticsKind, string> = {
  dashboard: "Dashboard", "deep-dive": "Deep-dive", adhoc: "Ad-hoc",
};
function statusSeverity(s: AnalyticsStatus): "low" | "medium" | "high" {
  return s === "live" ? "low" : "medium";
}

type Tab = "all" | AnalyticsKind;

export default function Analytics() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => (tab === "all" ? ANALYTICS : ANALYTICS.filter((a) => a.kind === tab)), [tab]);
  const current = openId ? ANALYTICS.find((a) => a.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <Card testId="an-filters">
        <TabBar<Tab>
          testId="an-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${ANALYTICS.length}` },
            { key: "dashboard", label: "Dashboards" },
            { key: "deep-dive", label: "Deep-dives" },
            { key: "adhoc",     label: "Ad-hoc" },
          ]}
        />
      </Card>

      <Card padded={false} testId="an-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Analytics Catalogue" title={`${filtered.length} board${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Directory of every dashboard, deep-dive and ad-hoc analysis that exists across ORVAN. Boards are not rendered inline in this release — this is a catalogue.</div>
        </div>
        <div className="p-2">
          {filtered.map((a) => (
            <ListRow key={a.id} testId={`an-${a.id}`}
              onClick={() => setOpenId(a.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{KIND_LABEL[a.kind]}</span>
                  <span>{a.title}</span>
                </span>
              }
              sublabel={<>{a.category} · {a.owner} · {a.kpiCount} KPIs · refreshed {a.refreshRelative}</>}
              trailing={<SeverityBadge severity={statusSeverity(a.status)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="an-drawer" title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${KIND_LABEL[current.kind]} · ${current.category}`} title={current.owner} />
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>KPIs</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.kpiCount}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Refreshed</div><div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.refreshRelative}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Status</div><div className="mt-2"><SeverityBadge severity={statusSeverity(current.status)} /></div></div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
